import { formatInTimeZone } from 'date-fns-tz';
import { formatDateInTimezone, getOrderTimezone } from '@/utilities/addFormattedDateFields';
import { DELIVERY_TABS } from './constants';

const COMPLETE_ORDER_STATUSES = ['complete', 'routedComplete'];

export const getInventoryCursor = (orders) => {
    const lastOrder = orders[orders.length - 1];

    // created_at is nullable; a null boundary can't be compared, so stop paging instead of emitting a broken predicate
    if (!lastOrder?.created_at) {
        return null;
    }

    return {
        created_at: lastOrder.created_at,
        order_id: lastOrder.order_id,
    };
};

export const getInventoryCursorWhere = ({ created_at, order_id }) => ({
    _or: [
        { created_at: { _lt: created_at } },
        {
            _and: [{ created_at: { _eq: created_at } }, { order_id: { _lt: order_id } }],
        },
    ],
});

export const getCurrentItemException = (mappings = []) => {
    const latestMapping = mappings.reduce((latest, mapping) => {
        const hasException = !!mapping.exception;
        const isItemException = mapping.exception_type === 'ITEM';
        const isUnresolved = mapping.exception?.status !== 'RESOLVED';
        const isNewer = !latest || new Date(mapping.created_at).getTime() > new Date(latest.created_at).getTime();

        return hasException && isItemException && isUnresolved && isNewer ? mapping : latest;
    }, null);

    return latestMapping?.exception || null;
};

// A pallet's logs are shared by every item sitting on it, so pushing one entry per item would count
// the same warehouse event once per item. Keep a single entry per log row and accumulate the
// quantity of this SKU row's items on that pallet.
export const mergePalletLog = (palletLogs, log, item) => {
    const existing = palletLogs.find((entry) => entry.log_id === log.log_id);
    if (existing) {
        existing.quantity += item.quantity;
        return palletLogs;
    }

    palletLogs.push({
        ...log,
        quantity: item.quantity,
        item_id: item.item_id,
        pallet_label: item.pallet.pallet_name || item.pallet.pallet_number,
        pallet_id: log.pallet_id,
    });

    return palletLogs;
};

// Keep only the rows where a pallet's status actually changed, so a running quantity ledger counts
// each transition once instead of once per write. Collapse before filtering by status, otherwise a
// genuine RECEIVED -> STORED -> RECEIVED round trip would read as a single receive.
export const collapsePalletLogTransitions = (logs = []) => {
    const lastStatusByPallet = new Map();
    return [...logs]
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .filter((log) => {
            const previous = lastStatusByPallet.get(log.pallet_id);
            lastStatusByPallet.set(log.pallet_id, log.warehouse_status);
            return previous !== log.warehouse_status;
        });
};

const IN_STATUSES = ['RECEIVED'];
const OUT_STATUSES = ['STAGED', 'PICKED_UP'];

/**
 * Running in/out quantities for a SKU's pallet history, one entry per direction per pallet.
 * A pallet's quantity can only arrive once and leave once, so the later writes of the same
 * direction are dropped rather than added again — STAGED then PICKED_UP is one departure, and a
 * RECEIVED -> STORED -> RECEIVED round trip is one arrival. Date Out uses the first out
 * transition, matching the Date Out column on the table, which reads the STAGED log.
 */
export const buildPalletLogLedger = (logs = []) => {
    const counted = { in: new Set(), out: new Set() };
    let qtyIn = 0;
    let qtyOut = 0;

    return collapsePalletLogTransitions(logs).flatMap((log) => {
        const direction = IN_STATUSES.includes(log.warehouse_status)
            ? 'in'
            : OUT_STATUSES.includes(log.warehouse_status)
            ? 'out'
            : null;

        if (!direction || counted[direction].has(log.pallet_id)) return [];
        counted[direction].add(log.pallet_id);

        if (direction === 'in') {
            qtyIn += log.quantity;
            return [{ action: 'Date In', created_at: log.created_at, qtyIn, qtyOut: null }];
        }

        qtyOut += log.quantity;
        return [{ action: 'Date Out', created_at: log.created_at, qtyIn: null, qtyOut }];
    });
};

export const getInventorySkuStatus = (order, { receivedQuantity = 0, outQuantity = 0 }) => {
    if (!receivedQuantity) {
        return 'NOT_RECEIVED';
    }

    const qtyOnHand = receivedQuantity - outQuantity;

    if (order?.order_type === 'delivery' && COMPLETE_ORDER_STATUSES.includes(order?.order_status)) {
        return 'COMPLETE';
    }

    if (qtyOnHand <= 0 && outQuantity > 0) {
        return 'COMPLETE';
    }

    if (qtyOnHand > 0) {
        return 'IN_STOCK';
    }

    return 'COMPLETE';
};

export const processOrders = (orders, tabIndex, search = '') => {
    const skuMap = orders.reduce((acc, order) => {
        let nullSkuCounter = 1;
        let orderTimezone;
        const getScheduledDeliveryDate = (scheduledDelivery) => {
            if (!scheduledDelivery) {
                return null;
            }
            // zip lookup is the expensive part — defer it until an outbound manifest actually needs it
            if (!orderTimezone) {
                orderTimezone = getOrderTimezone(order);
            }
            return formatDateInTimezone(scheduledDelivery, orderTimezone, 'MM/dd/yyyy');
        };

        //filter out extraneous items if there is a direct sku match to search term
        const matchingSkuItems = order.itemsByOrderId.filter(
            (item) => item.sku && item.sku.toLowerCase().includes(search.trim().toLowerCase())
        );
        const itemsToProcess = matchingSkuItems.length > 0 ? matchingSkuItems : order.itemsByOrderId;

        itemsToProcess.forEach((item) => {
            let sku = item.sku;

            if (!sku) {
                sku = `no_sku_${nullSkuCounter++}`;
            }
            const currentItemException = getCurrentItemException(item.exceptions);
            const uniqueKey = `${order.order_id}_${sku}`;
            if (acc.has(uniqueKey)) {
                const existingItem = acc.get(uniqueKey);
                existingItem.totalQuantity += item.quantity;
                existingItem.receivedQuantity +=
                    item.pallet && item.pallet.warehouse_status !== 'NOT_RECEIVED' ? item.quantity : 0;
                existingItem.outQuantity +=
                    item.pallet &&
                    (item.pallet.warehouse_status === 'STAGED' || item.pallet.warehouse_status === 'PICKED_UP')
                        ? item.quantity
                        : 0;
                if (item.pallet && !existingItem.palletIds.has(item.pallet.pallet_id)) {
                    existingItem.palletIds.add(item.pallet.pallet_id);
                    existingItem.pallets.push(item.pallet);
                }
                if (
                    item.pallet &&
                    item.pallet.warehouse_location &&
                    !existingItem.wh_locations.has(item.pallet.warehouse_location)
                ) {
                    existingItem.wh_locations.add(item.pallet.warehouse_location);
                }
                if (item.pallet && item.pallet.logs) {
                    item.pallet.logs.forEach((log) => mergePalletLog(existingItem.palletLogs, log, item));
                }

                if (item.pallet) {
                    const palletId = item.pallet.pallet_id;
                    if (!existingItem.individualPalletInfo[palletId]) {
                        existingItem.individualPalletInfo[palletId] = {
                            totalQuantity: 0,
                            receivedQuantity: 0,
                            outQuantity: 0,
                            warehouseStatus: item.pallet.warehouse_status,
                        };
                    }
                    existingItem.individualPalletInfo[palletId].totalQuantity += item.quantity;
                    existingItem.individualPalletInfo[palletId].receivedQuantity +=
                        item.pallet.warehouse_status !== 'NOT_RECEIVED' ? item.quantity : 0;
                    existingItem.individualPalletInfo[palletId].outQuantity +=
                        item.pallet.warehouse_status === 'STAGED' || item.pallet.warehouse_status === 'PICKED_UP'
                            ? item.quantity
                            : 0;
                }

                item.manifests.forEach((manifestMapping) => {
                    const manifest = manifestMapping.manifest;
                    if (manifest) {
                        if (['INBOUND', 'CROSS_DOCK'].includes(manifest.type)) {
                            existingItem.dateIn = manifest.receiving_date
                                ? formatInTimeZone(new Date(manifest.receiving_date), 'UTC', 'EEE, MMM d, yyyy')
                                : '-';
                            existingItem.receivedDate = manifest.receiving_date || null;
                            if (!existingItem.inboundManifests.includes(manifest.manifest_number)) {
                                existingItem.inboundManifests.push(manifest.manifest_number);
                            }
                        } else if (['OUTBOUND', 'RETURN_TO_SENDER', 'WILL_CALL'].includes(manifest.type)) {
                            existingItem.dateOut =
                                getScheduledDeliveryDate(manifest?.route?.scheduled_delivery) ||
                                (manifest.receiving_date
                                    ? formatInTimeZone(new Date(manifest.receiving_date), 'UTC', 'EEE, MMM d, yyyy')
                                    : '-');
                            if (!existingItem.outboundManifests.includes(manifest.manifest_number)) {
                                existingItem.outboundManifests.push(manifest.manifest_number);
                            }
                        }
                    }
                });

                if (currentItemException) {
                    existingItem.exceptions.push(currentItemException);
                }
            } else {
                const newItem = {
                    sku: sku,
                    order_status: order.order_status,
                    order_type: order.order_type,
                    totalQuantity: item.quantity,
                    receivedQuantity:
                        item.pallet && item.pallet.warehouse_status !== 'NOT_RECEIVED' ? item.quantity : 0,
                    outQuantity:
                        item.pallet &&
                        (item.pallet.warehouse_status === 'STAGED' || item.pallet.warehouse_status === 'PICKED_UP')
                            ? item.quantity
                            : 0,
                    order_number: order.order_number,
                    po_number: order.po_number,
                    shipper: order.order_shipper?.business_name || '-',
                    dateIn: '-',
                    receivedDate: null,
                    dateOut: '-',
                    inboundManifests: [],
                    outboundManifests: [],
                    pallets: item.pallet ? [item.pallet] : [],
                    palletIds: new Set(item.pallet ? [item.pallet.pallet_id] : []),
                    wh_locations: new Set(item.pallet?.warehouse_location ? [item.pallet.warehouse_location] : []),
                    palletLogs: (item.pallet?.logs || []).reduce((acc, log) => mergePalletLog(acc, log, item), []),
                    individualPalletInfo: {},
                    exceptions: currentItemException ? [currentItemException] : [],
                };

                if (item.pallet) {
                    const palletId = item.pallet.pallet_id;
                    newItem.individualPalletInfo[palletId] = {
                        warehouseStatus: item.pallet.warehouse_status,
                        totalQuantity: item.quantity,
                        receivedQuantity: item.pallet.warehouse_status !== 'NOT_RECEIVED' ? item.quantity : 0,
                        outQuantity:
                            item.pallet.warehouse_status === 'STAGED' || item.pallet.warehouse_status === 'PICKED_UP'
                                ? item.quantity
                                : 0,
                    };
                }

                item.manifests.forEach((manifestMapping) => {
                    const manifest = manifestMapping.manifest;
                    if (manifest) {
                        if (['INBOUND', 'CROSS_DOCK'].includes(manifest.type)) {
                            newItem.dateIn = manifest.receiving_date
                                ? formatInTimeZone(new Date(manifest.receiving_date), 'UTC', 'EEE, MMM d, yyyy')
                                : '-';
                            newItem.receivedDate = manifest.receiving_date || null;
                            if (!newItem.inboundManifests.includes(manifest.manifest_number)) {
                                newItem.inboundManifests.push(manifest.manifest_number);
                            }
                        } else if (['OUTBOUND', 'RETURN_TO_SENDER', 'WILL_CALL'].includes(manifest.type)) {
                            newItem.dateOut =
                                getScheduledDeliveryDate(manifest?.route?.scheduled_delivery) ||
                                (manifest.receiving_date
                                    ? formatInTimeZone(new Date(manifest.receiving_date), 'UTC', 'EEE, MMM d, yyyy')
                                    : '-');
                            if (!newItem.outboundManifests.includes(manifest.manifest_number)) {
                                newItem.outboundManifests.push(manifest.manifest_number);
                            }
                        }
                    }
                });

                acc.set(uniqueKey, newItem);
            }
        });
        return acc;
    }, new Map());

    const filteredItems = Array.from(skuMap.values()).map((item) => {
        item.palletLogs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        item.wh_locations = Array.from(item.wh_locations);
        item.status = getInventorySkuStatus(
            { order_status: item.order_status, order_type: item.order_type },
            { receivedQuantity: item.receivedQuantity, outQuantity: item.outQuantity }
        );
        return item;
    });

    const statusFilter = DELIVERY_TABS[tabIndex].statusFilter;
    if (statusFilter) {
        return filteredItems.filter((item) => item.status === statusFilter);
    }

    return filteredItems;
};

export const processPallets = (itemsBySku, tabIndex) => {
    const pallets = itemsBySku.flatMap((item) => {
        const processPallet = (palletId) => {
            const palletLogs = item.palletLogs.filter((log) => log.pallet_id === palletId);
            const dateInLog = palletLogs.find((log) => log.warehouse_status === 'RECEIVED');
            const dateOutLog = palletLogs.find((log) => log.warehouse_status === 'STAGED');

            return {
                ...item,
                palletId,
                pallets: item.pallets.filter((pallet) => pallet.pallet_id === palletId),
                palletLogs,
                dateIn: dateInLog ? formatInTimeZone(new Date(dateInLog.created_at), 'UTC', 'EEE, MMM d, yyyy') : '-',
                receivedDate: dateInLog && !dateOutLog ? new Date(dateInLog.created_at) : null,
                dateOut: dateOutLog
                    ? formatInTimeZone(new Date(dateOutLog.created_at), 'UTC', 'EEE, MMM d, yyyy')
                    : '-',
                exceptions: item.exceptions.filter((exception) =>
                    item.pallets.some((pallet) => pallet.pallet_id === palletId)
                ),
            };
        };

        if (item?.palletIds?.size > 1) {
            return Array.from(item.palletIds).map(processPallet);
        }

        if (item?.palletIds?.size === 1) {
            const palletId = Array.from(item.palletIds)[0];
            return processPallet(palletId);
        }

        return item;
    });

    // Filter if on 'in stock' tab
    if (tabIndex === 2) {
        return pallets.filter((palletItem) =>
            palletItem.pallets.some((pallet) => ['STORED', 'RECEIVED'].includes(pallet.warehouse_status))
        );
    }

    return pallets;
};
